/**
 * The workspace tab bar.
 *
 * From the mockup: **Overview · People · Planning · Selection Policy · Access · History · Exports ·
 * Attachments**. One component rather than a row of links repeated on eight screens, so the set
 * cannot drift apart — a tab that exists on Planning but not on People is how a surface quietly
 * becomes unreachable.
 *
 * The participant view is deliberately **not** here. It has no navigation at all: somebody planning
 * a funeral through a shared link is not browsing a workspace.
 *
 * Every tab is always shown. Route-level hiding is presentation, not security: the server decides
 * what each of these pages returns, and a page somebody may not see returns 404 there regardless of
 * whether this bar offered the link (docs/engineering/04-authorization.md). Hiding tabs by guessed
 * authority would only produce a navigation that disagrees with itself between screens.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import './workspace-tabs.css';

const TABS = [
  { to: '/liturgies/$id', key: 'overview' },
  { to: '/liturgies/$id/people', key: 'people' },
  { to: '/liturgies/$id/planning', key: 'planning' },
  { to: '/liturgies/$id/selection-policy', key: 'selection_policy' },
  { to: '/liturgies/$id/access', key: 'access' },
  { to: '/liturgies/$id/history', key: 'history' },
  { to: '/liturgies/$id/exports', key: 'exports' },
  { to: '/liturgies/$id/attachments', key: 'attachments' },
] as const;

export interface WorkspaceTabsProps {
  liturgyId: string;
}

export function WorkspaceTabs({ liturgyId }: WorkspaceTabsProps): React.JSX.Element {
  const { t } = useTranslation('common');

  return (
    <nav className="wtabs" aria-label={t('workspace.overview')}>
      {TABS.map((tab) => (
        <Link
          key={tab.to}
          className="wtabs__tab"
          activeProps={{ className: 'wtabs__tab wtabs__tab--on' }}
          // Otherwise "/liturgies/{id}" matches as active on every child route, and the bar shows
          // two current tabs at once.
          activeOptions={{ exact: true }}
          to={tab.to}
          params={{ id: liturgyId }}
        >
          {t(`workspace.${tab.key}`)}
        </Link>
      ))}
    </nav>
  );
}
